import { DEFAULT_MARKUP } from '../ui/markup'
import { type Failure, RequestFailed, csrfHeaders, malformed, readJson, request } from './request'
import type { SubjectOf } from './subjects'

/**
 * The library, as the service describes it.
 *
 * Everything in here mirrors what `/api/library` answers, and nothing is worked out a second time on
 * this side. Two of those are worth naming, because the temptation to decide them here is real:
 *
 * - **`canBeShownInBrowser`** says whether a file may be put into the page. It is a question about
 *   safety — an HTML file shown inline runs on MyLib's own origin — and the service answers it. This
 *   side never guesses from a file name.
 * - **`limits`** says what this installation takes. It is read rather than assumed, so that an
 *   oversized file is refused here, before ten minutes are spent uploading it, without there being a
 *   second copy of the number to keep in step.
 */

export interface LibraryFolder {
  readonly id: string
  /** `null` means the folder sits at the top of the library. */
  readonly parentId: string | null
  readonly name: string
  /** How many documents lie directly in it — not counting its subfolders. */
  readonly documents: number
  readonly createdAt: string
  readonly updatedAt: string
  /**
   * The subjects this folder belongs to.
   *
   * Anchoring a folder is the most effective single act in that area — everything in it and below it
   * belongs to the subject afterwards — so the shelf shows it where the folder is.
   */
  readonly subjects?: readonly SubjectOf[]
}

export interface LibraryDocument {
  readonly id: string
  /** `null` means it has not been sorted yet. */
  readonly folderId: string | null
  readonly title: string
  readonly fileName: string
  readonly mediaType: string
  /** The extension, upper-cased. What a tile shows when there is no picture of the content. */
  readonly kind: string
  readonly sizeInBytes: number
  readonly createdAt: string
  readonly updatedAt: string
  readonly takenOverAt: string
  /** Which version this is, counted inside its own line. `1` for a document nothing has happened to. */
  readonly version: number
  /** Which version is being shown. */
  readonly versionId: string
  /**
   * What the content is, exactly — the checksum of the bytes being shown.
   *
   * A whole editing session writes into one version, so the version alone does not say when the
   * content changed. This does, and it is what the picture's address is keyed by.
   */
  readonly contentId: string
  /** `source` for a file that arrived, `own` for something written here. */
  readonly line: VersionLine
  /**
   * Which markup this document is written in, or `null` when it is not something to write in.
   *
   * It is what decides whether the editor is offered at all, and which renderer draws the preview. A
   * property of the content and not of the editor — which is what makes a second markup language an
   * entry in a lookup rather than a second editor.
   */
  readonly format: string | null
  /** Whether what is shown is somebody's own writing rather than the file that arrived. */
  readonly written: boolean
  /** Whether there is an original behind the editing — a file this document also is. */
  readonly hasAnOriginal: boolean
  /** The newest version that arrived from outside, where there is one. What "the original" means. */
  readonly sourceVersionId: string | null
  readonly hasEarlierVersions: boolean
  /** How it came into MyLib — one entry per route, and there can be more than one. */
  readonly origins: readonly DocumentOrigin[]
  /** The file has disappeared where it came from. The document stays; this is a mark on it. */
  readonly goneFromSource: boolean
  readonly canBeShownInBrowser: boolean
  readonly hasPreview: boolean
  /**
   * How far the search has got with this document's content.
   *
   * `indexed` is the ordinary case and says nothing worth showing. The other four are the reason this
   * travels with every document: a document that is not found has to be able to say why, instead of
   * being silently absent from a result somebody was relying on. See `whyItMightNotBeFound`.
   */
  readonly textState: TextState
  /**
   * The subjects it belongs to — the ones somebody filed it under and the ones it inherits from the
   * folder it lies in.
   *
   * Each says which of the two it is, because only a direct belonging can be undone where it is
   * shown: an inherited one is a statement about a folder.
   */
  readonly subjects?: readonly SubjectOf[]
}

/**
 * `without-text` is a scan with no text layer or a photograph before its text has been recognised —
 * **not a failure**, and a state on the way rather than a final one.
 */
export type TextState = 'pending' | 'indexing' | 'indexed' | 'without-text' | 'failed' | string

export type OriginKind = 'upload' | 'connected-system' | 'photo' | 'authored' | string

/**
 * Which of a document's two lines a version belongs to.
 *
 * `source` is what arrived — an upload, or a sync of the same material. `own` is what somebody wrote
 * here, one version per editing session. What a document *shows* is its newest `own` version when there
 * is one, and the newest `source` version otherwise, which is the whole of "I can edit an uploaded
 * document without losing the original".
 */
export type VersionLine = 'source' | 'own'

/** One route a document came by. */
export interface DocumentOrigin {
  readonly kind: OriginKind
  /** The system it came out of, where there is one. Empty otherwise. */
  readonly source: string
  /**
   * Whereabouts in that system it was — a path, as that system spells it. Empty where there is none.
   *
   * Something to read and not something to click: ILIAS hands out no object id over WebDAV, so there
   * is no address to go back to and a link would be a promise nothing can keep.
   */
  readonly location: string
  readonly firstSeenAt: string
  /** When that source was last looked at. `null` for a route that is not synced. */
  readonly lastSyncedAt: string | null
  /** When a sync found the file gone from there. `null` while it is still there. */
  readonly goneFromSourceAt: string | null
}

/** One version of a document: a file, and when it arrived or was written. */
export interface DocumentVersion {
  readonly id: string
  /** 1 for the first version **of its line**. The numbers repeat between the two lines. */
  readonly ordinal: number
  /** Which line it belongs to, which is what makes its number mean anything. */
  readonly line: VersionLine
  readonly fileName: string
  readonly mediaType: string
  readonly kind: string
  readonly sizeInBytes: number
  readonly takenOverAt: string
  readonly current: boolean
}

/**
 * The text of one version, ready to be put into the editor.
 *
 * {@link basedOn} is what goes back with a save, and it is the whole of how two tabs editing the same
 * document are told apart: it is the checksum of *this text* rather than the version's id, because a
 * whole editing session writes into one version.
 */
export interface DocumentText {
  readonly text: string
  readonly versionId: string
  readonly basedOn: string
  readonly mediaType: string
  readonly line: VersionLine
  /** The file was not valid UTF-8 and was read as Latin-1. Saving it back writes UTF-8. */
  readonly readAsFallbackEncoding: boolean
}

/** What one save produced. {@link basedOn} is what the editor carries from here on. */
export interface SavedText {
  readonly document: LibraryDocument
  readonly writtenAs: string
  readonly basedOn: string
}

export interface LibraryLimits {
  readonly maxFileSizeBytes: number
  readonly maxArchiveSizeBytes: number
}

export interface Library {
  readonly folders: readonly LibraryFolder[]
  /**
   * How many documents are in no folder.
   *
   * Nothing draws it any more: those documents lie at the top of the library, so their number is simply
   * how many are listed there. The service still sends it — it goes when the service is next opened.
   */
  readonly unsorted: number
  /** How many things are in the trash. `0` means there is nothing to offer a way into. */
  readonly trash: number
  readonly limits: LibraryLimits
}

/** What became of one file that was handed in. */
export interface Taken {
  readonly document: LibraryDocument
  /**
   * The content was already in the library, so this is the document it belongs to.
   *
   * Not a failure and not a warning: the file *is* in MyLib afterwards, which is what was asked for.
   * Worth saying out loud, though — "added" and "you already had this" are different answers.
   */
  readonly merged: boolean
}

export type TrashKind = 'document' | 'folder'

/** One line in the trash: an act of deleting, and how much it took. */
export interface TrashEntry {
  readonly kind: TrashKind
  readonly id: string
  readonly name: string
  readonly deletedAt: string
  /** How many documents went with it. `0` for a document, which is only itself. */
  readonly holds: number
}

export interface RefusedEntry {
  readonly name: string
  readonly reason: 'too-large' | 'not-a-path' | 'too-many' | string
  readonly sizeInBytes: number | null
}

export interface ArchiveImport {
  readonly imported: readonly Taken[]
  readonly refused: readonly RefusedEntry[]
  readonly stoppedEarly: boolean
}

const LIBRARY = '/api/library'

export interface WhichFile {
  /** Ask for it to be saved rather than shown in the page. */
  readonly download?: boolean
  /** An earlier version of the document. Absent means the current one. */
  readonly version?: string
}

/** Where a document's file is — the current one, or a named earlier version of it. */
export function contentUrl(document: LibraryDocument, which: WhichFile = {}): string {
  const asked = [
    which.download === true ? 'download=true' : null,
    which.version === undefined ? null : `version=${encodeURIComponent(which.version)}`,
  ].filter((one) => one !== null)
  const query = asked.length === 0 ? '' : `?${asked.join('&')}`
  return `${LIBRARY}/documents/${encodeURIComponent(document.id)}/content${query}`
}

/**
 * Where the picture of a document's content is — its first page, or the photo itself.
 *
 * **The content's own identity is in the address.** Without it the address never changes, and a
 * browser does not ask again for an image it already holds under the same one — so a document that
 * had been edited went on showing the page it had before. The service answers from the checksum
 * either way; this is what makes the browser go and ask.
 */
export function previewUrl(document: LibraryDocument): string {
  const content = encodeURIComponent(document.contentId)
  return `${LIBRARY}/documents/${encodeURIComponent(document.id)}/preview?content=${content}`
}

export async function fetchLibrary(signal?: AbortSignal): Promise<Library> {
  const payload = await readJson<Partial<Library>>(await request(LIBRARY, { signal }))

  if (!Array.isArray(payload.folders) || typeof payload.limits?.maxFileSizeBytes !== 'number') {
    throw malformed('The answer did not describe a library.')
  }

  return {
    folders: payload.folders,
    unsorted: typeof payload.unsorted === 'number' ? payload.unsorted : 0,
    trash: typeof payload.trash === 'number' ? payload.trash : 0,
    limits: payload.limits,
  }
}

/**
 * What lies in a folder, or — with `folder` `null` — what lies in no folder at all.
 *
 * The second case is what the top of the library shows, beside the folders. A document in no folder is
 * not somewhere else; it is simply not filed, and that is seen by where it lies.
 */
export async function fetchDocuments(
  folder: string | null,
  signal?: AbortSignal,
): Promise<LibraryDocument[]> {
  const address =
    folder === null
      ? `${LIBRARY}/documents`
      : `${LIBRARY}/documents?folder=${encodeURIComponent(folder)}`
  const payload = await readJsonArray(await request(address, { signal }))
  return payload as LibraryDocument[]
}

/**
 * One document, by its id alone.
 *
 * The one request that does not start from a place. Everything else on this screen asks "what lies
 * here"; this asks "where does this lie", and it is what lets somebody arrive from a link, a subject
 * or a board — all of which know a kind and an id and deliberately nothing else — and still land in
 * the folder the document is actually in.
 */
export async function fetchDocument(id: string, signal?: AbortSignal): Promise<LibraryDocument> {
  const address = `${LIBRARY}/documents/${encodeURIComponent(id)}`
  return await readJson<LibraryDocument>(await request(address, { signal }))
}

export async function createFolder(parentId: string | null, name: string): Promise<LibraryFolder> {
  return (await change(`${LIBRARY}/folders`, 'POST', { parentId, name })) as LibraryFolder
}

export async function renameFolder(id: string, name: string): Promise<void> {
  await change(`${LIBRARY}/folders/${encodeURIComponent(id)}/name`, 'PUT', { name })
}

export async function moveFolder(id: string, parentId: string | null): Promise<void> {
  await change(`${LIBRARY}/folders/${encodeURIComponent(id)}/place`, 'PUT', { parentId })
}

export async function deleteFolder(id: string): Promise<void> {
  await change(`${LIBRARY}/folders/${encodeURIComponent(id)}`, 'DELETE')
}

export async function renameDocument(id: string, title: string): Promise<LibraryDocument> {
  const address = `${LIBRARY}/documents/${encodeURIComponent(id)}/title`
  return (await change(address, 'PUT', { title })) as LibraryDocument
}

export async function moveDocument(id: string, folderId: string | null): Promise<LibraryDocument> {
  const address = `${LIBRARY}/documents/${encodeURIComponent(id)}/place`
  return (await change(address, 'PUT', { folderId })) as LibraryDocument
}

/** Moves the document to the trash. It stays there until the trash is emptied. */
export async function deleteDocument(id: string): Promise<void> {
  await change(`${LIBRARY}/documents/${encodeURIComponent(id)}`, 'DELETE')
}

/** Every version of a document, newest first. Asked for only when there is a history to show. */
export async function fetchVersions(id: string, signal?: AbortSignal): Promise<DocumentVersion[]> {
  const address = `${LIBRARY}/documents/${encodeURIComponent(id)}/versions`
  return (await readJsonArray(await request(address, { signal }))) as DocumentVersion[]
}

/**
 * Hands in a file as a newer version of a document that is already there.
 *
 * The same thing a sync does: the material has changed, so it becomes another version of the same
 * document rather than a second document — and everything pointing at the document keeps pointing at
 * it. A file identical to the current one adds nothing, and the answer says so.
 */
export function addVersion(document: string, file: File, sent: Sent): Promise<Taken> {
  return upload<Taken>(
    `${LIBRARY}/documents/${encodeURIComponent(document)}/versions`,
    file,
    null,
    sent,
  )
}

/**
 * A document written here rather than uploaded.
 *
 * JSON where an upload is multipart, which is what tells the two apart at the service: there is no file,
 * because the document *is* the text somebody is about to type.
 */
export async function createDocument(
  folderId: string | null,
  title: string,
  format = DEFAULT_MARKUP,
): Promise<LibraryDocument> {
  return (await change(`${LIBRARY}/documents`, 'POST', {
    folderId,
    title,
    format,
  })) as LibraryDocument
}

/**
 * The text of a document, for the editor.
 *
 * `version` names one, which is how the original is read while somebody's own writing stands in front of
 * it. Its own endpoint rather than the content one: the answer says what the text is *based on*, which
 * is what a save has to carry.
 */
export async function fetchText(
  id: string,
  version?: string,
  signal?: AbortSignal,
): Promise<DocumentText> {
  const address =
    `${LIBRARY}/documents/${encodeURIComponent(id)}/text` +
    (version === undefined ? '' : `?version=${encodeURIComponent(version)}`)
  const payload = await readJson<Partial<DocumentText>>(await request(address, { signal }))
  if (typeof payload.text !== 'string' || typeof payload.basedOn !== 'string') {
    throw malformed('The answer did not describe a text.')
  }
  return payload as DocumentText
}

/**
 * One save out of the editor. **The one place a document's text is written.**
 *
 * `basedOn` is what the editor last read or wrote; a document that has moved on since refuses the save
 * with `edit-out-of-date`, and the editor goes read-only rather than losing what was typed.
 * `beginsASession` is set on the first save after the editor was opened and on no other, which is what
 * makes one editing session one version in the history.
 */
export async function saveText(
  id: string,
  text: string,
  basedOn: string,
  beginsASession: boolean,
): Promise<SavedText> {
  return (await change(`${LIBRARY}/documents/${encodeURIComponent(id)}/text`, 'PUT', {
    text,
    basedOn,
    beginsASession,
  })) as SavedText
}

/**
 * The same document, written in another markup from now on.
 *
 * **Nothing but the language is sent**, because nothing but the language changes: not a byte of the
 * text, no version in the history. The file the document shows is renamed to match — `.md` becomes
 * `.adoc` — which the service does, because a name is a fact about the file and not about the text.
 *
 * Its own address rather than a field on {@link saveText}: that one is called while somebody types,
 * and a language that could change on a keystroke is not a language anybody chose.
 */
export async function changeMarkup(id: string, format: string): Promise<LibraryDocument> {
  return (await change(`${LIBRARY}/documents/${encodeURIComponent(id)}/markup`, 'PUT', {
    format,
  })) as LibraryDocument
}

/** Throws away everything written here, so the document is its original again. */
export async function discardOwnLine(id: string): Promise<LibraryDocument> {
  return (await change(
    `${LIBRARY}/documents/${encodeURIComponent(id)}/text`,
    'DELETE',
  )) as LibraryDocument
}

/**
 * Whether this document is something the editor can open at all.
 *
 * Asked of the *document*, which carries its format because the service worked it out from the file —
 * never guessed here from a name. An answer that is missing altogether counts as "no": an older service
 * that says nothing about formats has no documents to write in.
 */
export function canBeWrittenIn(document: LibraryDocument): boolean {
  return typeof document.format === 'string' && document.format !== ''
}

export async function fetchTrash(signal?: AbortSignal): Promise<TrashEntry[]> {
  return (await readJsonArray(await request(`${LIBRARY}/trash`, { signal }))) as TrashEntry[]
}

/** Puts an entry back where it was, with everything that went to the trash with it. */
export async function restore(entry: TrashEntry): Promise<void> {
  await change(`${LIBRARY}/trash/${entry.kind}/${encodeURIComponent(entry.id)}/restore`, 'POST')
}

/** Removes one entry, and everything that went with it, for good. */
export async function removeForGood(entry: TrashEntry): Promise<void> {
  await change(`${LIBRARY}/trash/${entry.kind}/${encodeURIComponent(entry.id)}`, 'DELETE')
}

export async function emptyTrash(): Promise<void> {
  await change(`${LIBRARY}/trash`, 'DELETE')
}

/** Whether this file is an archive, and therefore goes in to be unpacked rather than kept as one file. */
export function isArchive(file: File): boolean {
  return file.name.toLowerCase().endsWith('.zip')
}

/** The limit that applies to this file — archives are allowed to be larger, being containers. */
export function limitFor(file: File, limits: LibraryLimits): number {
  return isArchive(file) ? limits.maxArchiveSizeBytes : limits.maxFileSizeBytes
}

/** How far an upload has got, from 0 to 1. `null` while the browser cannot say. */
export type Sent = (fraction: number | null) => void

/**
 * Uploads one file and reports how far it has got.
 *
 * `XMLHttpRequest` rather than `fetch`, for the one thing it can still do that `fetch` cannot: say how
 * much of the *request* body has gone out. A file of two hundred megabytes on a lecture-hall
 * connection without that is a screen that has stopped, and the difference between "working" and
 * "stuck" is exactly what somebody watching an upload wants to know.
 */
export function uploadFile(file: File, folder: string | null, sent: Sent): Promise<Taken> {
  return upload<Taken>(`${LIBRARY}/documents`, file, folder, sent)
}

/** Uploads an archive to be unpacked, and answers with what came in and what did not. */
export function importArchive(
  file: File,
  folder: string | null,
  sent: Sent,
): Promise<ArchiveImport> {
  return upload<ArchiveImport>(`${LIBRARY}/archives`, file, folder, sent)
}

function upload<T>(path: string, file: File, folder: string | null, sent: Sent): Promise<T> {
  const address = folder === null ? path : `${path}?folder=${encodeURIComponent(folder)}`

  return new Promise<T>((resolve, reject) => {
    const call = new XMLHttpRequest()
    call.open('POST', address)
    call.setRequestHeader('Accept', 'application/json')
    for (const [name, value] of Object.entries(csrfHeaders('POST'))) {
      call.setRequestHeader(name, value)
    }

    call.upload.addEventListener('progress', (event) => {
      sent(event.lengthComputable ? event.loaded / event.total : null)
    })

    call.addEventListener('load', () => {
      if (call.status >= 200 && call.status < 300) {
        try {
          resolve(JSON.parse(call.responseText) as T)
        } catch {
          reject(malformed('The answer to the upload was not JSON.'))
        }
        return
      }
      reject(refusalIn(call.responseText, call.status))
    })

    call.addEventListener('error', () => {
      reject(
        new RequestFailed({
          kind: 'unreachable',
          message: 'MyLib is not answering, so the upload stopped.',
          detail: null,
          retryable: true,
        }),
      )
    })

    const body = new FormData()
    body.append('file', file)
    call.send(body)
  })
}

/**
 * One request that changes something, with the service's own refusals turned into something to read.
 *
 * The statuses listed here are answers rather than faults — that name is taken, that folder is gone,
 * that file is too large — so they are handed back instead of becoming a generic failure. The service
 * sends a code for each; the sentence somebody reads is written on this side, in the language the rest
 * of the screen is in.
 */
async function change(address: string, method: string, body?: unknown): Promise<unknown> {
  const response = await request(address, {
    method,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    expect: [...REFUSALS],
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })

  if (!response.ok) {
    throw refusalIn(await response.text(), response.status)
  }

  return response.status === 204 ? null : response.json()
}

/** The statuses that carry a code this side knows how to say out loud. */
const REFUSALS = [400, 404, 409, 413] as const

const SENTENCES: Readonly<Record<string, string>> = {
  'file-too-large': 'That file is larger than this MyLib installation accepts.',
  'archive-too-large': 'That archive is larger than this MyLib installation accepts.',
  'archive-unreadable': 'That file could not be read as a ZIP archive.',
  'no-such-folder': 'That folder is not there any more.',
  'no-such-document': 'That document is not there any more.',
  'name-in-use':
    'There is already a folder of that name here. A folder made since the deleted one went to the trash holds its name.',
  'folder-inside-itself': 'A folder cannot be moved into itself or into one of its own subfolders.',
  'not-a-name': 'That will not do as a name.',
  'no-such-trash-entry': 'That is not in the trash any more.',
  'content-gone': 'The file behind this document is missing from the store.',
  'not-something-to-write-in': 'This kind of file is kept as it is rather than written in.',
  'edit-out-of-date':
    'This document has changed somewhere else since you opened it — in another tab, or through a sync. Nothing you typed has been lost; refresh to carry on from the newer state.',
  'nothing-to-discard': 'There is nothing here to throw away.',
}

/** Whether a refusal is the one the editor answers by going read-only rather than by complaining. */
export function isOutOfDate(failure: Failure): boolean {
  return failure.code === 'edit-out-of-date'
}

function refusalIn(body: string, status: number): RequestFailed {
  const problem = parse(body)
  const known = problem?.code === undefined ? undefined : SENTENCES[problem.code]

  return new RequestFailed({
    kind: status >= 500 ? 'service' : 'refused',
    message: known ?? problem?.message ?? 'MyLib could not do that.',
    detail: known === undefined ? null : (problem?.message ?? null),
    // Nothing on this list gets better by being asked again: each of them is an answer.
    retryable: status >= 500,
    code: problem?.code ?? null,
  })
}

function parse(body: string): { code?: string; message?: string } | null {
  try {
    return JSON.parse(body) as { code?: string; message?: string }
  } catch {
    return null
  }
}

async function readJsonArray(response: Response): Promise<unknown[]> {
  const payload: unknown = await response.json().catch(() => null)
  if (!Array.isArray(payload)) {
    throw malformed('The answer was not a list.')
  }
  return payload
}
